import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "../auth/AuthContext";
import {
  cloudDeleteNotification,
  cloudListNotifications,
  cloudMarkAllNotificationsRead,
  cloudMarkNotificationRead,
  type NotificationDto,
} from "../api/client";

type NotificationsContextValue = {
  items: NotificationDto[];
  unread: number;
  loading: boolean;
  refresh: () => Promise<void>;
  markRead: (id?: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  remove: (id: string) => Promise<void>;
};

const NotificationsContext = createContext<NotificationsContextValue | null>(
  null,
);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState<NotificationDto[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setItems([]);
      setUnread(0);
      return;
    }
    setLoading(true);
    try {
      const data = await cloudListNotifications({ limit: 50 });
      setItems(data.items);
      setUnread(data.unread);
    } catch {
      /* offline or auth — keep last state */
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 30_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [refresh]);

  const markRead = useCallback(
    async (id?: string) => {
      await cloudMarkNotificationRead(id);
      await refresh();
    },
    [refresh],
  );

  const markAllRead = useCallback(async () => {
    await cloudMarkAllNotificationsRead();
    await refresh();
  }, [refresh]);

  const remove = useCallback(
    async (id: string) => {
      await cloudDeleteNotification(id);
      await refresh();
    },
    [refresh],
  );

  return (
    <NotificationsContext.Provider
      value={{ items, unread, loading, refresh, markRead, markAllRead, remove }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error("useNotifications must be used within NotificationsProvider");
  }
  return ctx;
}
